// src/modules/telegram/components/TelegramPreview/TelegramForwardPreview.tsx
import { FC } from "react"
import { Alert, Link, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"

type AnyBody = Record<string, unknown>

/**
 * Parses from_chat_id (@username) and the first numeric message_id from body.
 * Returns null when either field is missing or invalid.
 */
function parseForwardParts(
  body: AnyBody,
): { username: string; firstId: string } | null {
  const fromChatId = String(body?.from_chat_id ?? "").trim()
  if (!fromChatId.startsWith("@") || fromChatId.length < 2) return null

  const username = fromChatId.slice(1)

  const messageIdsRaw = String(body?.message_ids ?? "")
  const firstId = messageIdsRaw
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .find((s) => /^\d+$/.test(s))
  if (!firstId) return null

  return { username, firstId }
}

/**
 * Builds the Telegram embed URL for a forwarded post preview.
 *
 * Returns null when:
 *   - from_chat_id is not a @username (numeric ID or empty)
 *   - message_ids contains no valid positive integer
 *
 * Accepts a raw form-value map (all values may be strings or arrays)
 * so that both strict typed bodies and react-hook-form string outputs work.
 *
 * Exported for unit testing.
 */
export function buildForwardEmbedUrl(
  body: AnyBody,
  isDark: boolean,
  primaryHex: string,
): string | null {
  const parts = parseForwardParts(body)
  if (!parts) return null

  const { username, firstId } = parts

  const params = new URLSearchParams({
    embed: "1",
    mode: "tme",
    color: primaryHex,
    dark_color: primaryHex,
  })
  if (isDark) params.set("dark", "1")

  return `https://t.me/${username}/${firstId}?${params.toString()}`
}

interface TelegramForwardPreviewProps {
  body: AnyBody
}

/**
 * Renders a Telegram embed iframe for forwardMessages / copyMessages preview.
 * Uses the first message_id from the form field (shows full media group on Telegram side).
 * Only works when from_chat_id is a public @username.
 */
export const TelegramForwardPreview: FC<TelegramForwardPreviewProps> = ({
  body,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === "dark"
  const primaryHex = theme.palette.primary.main.replace("#", "")

  const parts = parseForwardParts(body)
  const url = parts ? buildForwardEmbedUrl(body, isDark, primaryHex) : null

  if (!url || !parts) {
    return (
      <Alert severity="warning">
        Укажите @username канала и ID сообщений для предпросмотра
      </Alert>
    )
  }

  const postUrl = `https://t.me/s/${parts.username}/${parts.firstId}`

  return (
    <>
      <Alert severity="info" sx={{ mb: 1 }}>
        Кнопки (inline-клавиатура) в этом предпросмотре не отображаются
      </Alert>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <Link href={postUrl} target="_blank" rel="noopener noreferrer">
          {postUrl}
        </Link>
      </Typography>
      <iframe
        src={url}
        style={{
          border: "none",
          width: "100%",
          height: "500px",
          display: "block",
        }}
        title="Telegram post preview"
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    </>
  )
}
